import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, Bed, Bath, Sofa, MapPin, ArrowUpRight, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (p, qual, display) => {
  if (!p || display === false) return 'POA';
  const num = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(p);
  const map = {
    'Offers Over': 'Offers Over ', 'Offers Around': 'Offers Around ',
    'Guide Price': 'Guide Price ', 'Fixed Price': 'Fixed Price ',
    'Offers In Excess Of': 'Offers In Excess Of ', 'From': 'From ',
  };
  return `${map[qual] || ''}${num}`;
};

/**
 * Homepage tile featuring one property from the Penny Lane feed.
 * Picks the highest-priced "For Sale" property with images (aspirational
 * showcase), falls back to the most recent Under Offer/Sold STC otherwise.
 */
const PropertyOfTheWeek = () => {
  const [p, setP] = useState(null);
  const [imgIdx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/pennylane/properties?limit=12&status=For Sale`);
        const d = await r.json();
        let pick = (d.items || []).filter((x) => (x.images || []).length >= 1);
        if (pick.length === 0) {
          const r2 = await fetch(`${API}/pennylane/properties?limit=6`);
          const d2 = await r2.json();
          pick = (d2.items || []).filter((x) => (x.images || []).length >= 1);
        }
        if (!alive || !pick.length) return;
        pick.sort((a, b) => (b.price || 0) - (a.price || 0));
        setP(pick[0]);
      } catch { /* empty */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!p || (p.images || []).length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % p.images.length), 4500);
    return () => clearInterval(id);
  }, [p]);

  if (!p) return null;
  const imgs  = p.images || [];
  const hero  = imgs[imgIdx]?.urls?.hero || imgs[imgIdx]?.url;

  return (
    <section className="bg-slate-50" data-testid="home-property-of-week">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-600 mb-1 inline-flex items-center gap-1.5">
              <Sparkles size={11}/> Property of the Week
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="home-potw-h2">
              Featured home for sale
            </h2>
          </div>
          <Link
            to="/for-sale"
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-cyan-700 transition-colors"
            data-testid="home-potw-cta"
          >
            Browse all 199 properties <ArrowUpRight size={13}/>
          </Link>
        </div>

        <Link
          to="/for-sale"
          className="group grid md:grid-cols-5 gap-0 rounded-2xl overflow-hidden bg-white ring-1 ring-slate-200 hover:shadow-2xl transition-all"
        >
          {/* Hero (3/5 cols) */}
          <div className="relative md:col-span-3 aspect-[4/3] md:aspect-auto bg-slate-200 overflow-hidden">
            {hero ? (
              <img src={hero} alt={p.inline_address} loading="lazy"
                   className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                   onError={(e) => { e.target.style.opacity = '0.2'; }}/>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400"><Home size={48}/></div>
            )}
            <span className="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-emerald-500 text-white">
              For Sale
            </span>
            {imgs.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1">
                {imgs.slice(0, Math.min(8, imgs.length)).map((_, i) => (
                  <span key={i} className={`h-1 rounded-full transition-all ${i === imgIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`}/>
                ))}
              </div>
            )}
          </div>

          {/* Details (2/5 cols) */}
          <div className="md:col-span-2 p-6 md:p-8 flex flex-col justify-center">
            <p className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              {formatPrice(p.price, p.price_qualifier, p.display_price)}
            </p>
            <h3 className="text-base font-semibold text-slate-700 mt-2 inline-flex items-start gap-2">
              <MapPin size={14} className="mt-1 text-cyan-600 shrink-0"/> {p.inline_address}
            </h3>

            <div className="flex items-center flex-wrap gap-x-5 gap-y-2 mt-5 text-sm text-slate-700">
              {p.bedrooms != null && (<span className="inline-flex items-center gap-1.5"><Bed size={14} className="text-cyan-600"/> <b>{p.bedrooms}</b></span>)}
              {p.bathrooms != null && (<span className="inline-flex items-center gap-1.5"><Bath size={14} className="text-cyan-600"/> <b>{p.bathrooms}</b></span>)}
              {p.receptions != null && (<span className="inline-flex items-center gap-1.5"><Sofa size={14} className="text-cyan-600"/> <b>{p.receptions}</b></span>)}
              {p.property_type && <span className="text-slate-500 text-xs">{p.property_type}</span>}
              {p.epc_rating && (<span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">EPC {p.epc_rating}</span>)}
            </div>

            {p.short_description && (
              <p className="mt-5 text-sm text-slate-600 line-clamp-3 leading-relaxed">
                {p.short_description}
              </p>
            )}

            <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Marketed by <b className="text-slate-800">{p.branch_name || 'Penny Lane Homes'}</b>
              </p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 group-hover:text-cyan-900 transition-colors">
                View details <ArrowUpRight size={12}/>
              </span>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
};

export default PropertyOfTheWeek;
